import React, { useState, useCallback } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Container } from '@crisellevaldez/speculo-ui';
import { StatsBox } from '../components/features/contacts/StatsBox';
import { useNavigate } from 'react-router-dom';
import { ContactFormModal } from '../components/features/contacts/ContactFormModal';
import { ContactTable } from '../components/features/contacts/ContactTable';
import { ContactTableHeader } from '../components/features/contacts/ContactTableHeader';
import { AddTagsModal } from '../components/features/contacts/AddTagsModal';
import { OffsetPagination } from '../components/common/OffsetPagination';
import { TableCard } from '../components/common/TableCard';
import { useContactsData } from '../hooks/useContactsData';

interface CallsStatsBoundaryProps {
  children: ReactNode;
}

interface CallsStatsBoundaryState {
  hasError: boolean;
}

class CallsStatsBoundary extends React.Component<
  CallsStatsBoundaryProps,
  CallsStatsBoundaryState
> {
  state: CallsStatsBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): CallsStatsBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Calls dashboard stats failed to render:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
          <h1 className="text-2xl font-black text-foreground">Calls Metrics</h1>
          <p className="mt-3 max-w-5xl text-sm leading-relaxed text-muted-foreground">
            Calls activity is still available below. The summary metrics failed to load for this
            organization, so the page is falling back to the calls table instead of crashing.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

const CallsDashboard: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);

  const {
    contacts,
    isLoading,
    error,
    totalContacts,
    searchQuery,
    searchField = '',
    selectedContacts,
    sortField,
    sortDirection,
    pageSize,
    currentPage,
    handleSelectAll,
    handleSelectContact,
    handleSort,
    handleSearch,
    handleDownload,
    handleAddContact,
    handleDeleteContact,
    handleDeleteSelected,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handlePageSizeChange,
    handleFilter,
    refetch,
  } = useContactsData();

  const handleEdit = useCallback((contact: any) => {
    // TODO: Implement edit logic
  }, []);

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        const success = await handleAddContact(data);
        if (success) {
          setIsModalOpen(false);
        }
      } catch (err) {
        throw err;
      }
    },
    [handleAddContact]
  );

  // Show other errors
  if (error instanceof Error) {
    return (
      <Container>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-red-600">Error loading contacts: {error.message}</p>
        </div>
      </Container>
    );
  }

  const navigate = useNavigate();

  const handleViewChange = (value: string | string[]) => {
    const selectedValue = Array.isArray(value) ? value[0] : value;
    switch (selectedValue) {
      case 'email':
        navigate('/dashboard/email');
        break;
      case 'calls':
        navigate('/calls');
        break;
      case 'all':
        navigate('/dashboard');
        break;
      default:
        navigate('/dashboard');
    }
  };

  return (
    <Container>
      <div className="flex flex-col space-y-10">
        <CallsStatsBoundary>
          <StatsBox
            title="Calls Metrics"
            showDashboardSwitch={true}
            currentView="calls"
            onViewChange={handleViewChange}
            description="Track and analyze your call performance with detailed metrics that provide insights into conversations, lead opportunities, and successful live transfers. Understand call outcomes, uncover areas for improvement and drive better results."
          />
        </CallsStatsBoundary>

        <div className="flex flex-col gap-2 xl:gap-6">
          <ContactTableHeader
            searchQuery={searchQuery}
            searchField={searchField}
            onSearchChange={handleSearch}
            onRefresh={refetch}
            onDownload={handleDownload}
            onAddContact={() => setIsModalOpen(true)}
            onFilter={handleFilter}
            isLoading={isLoading}
            totalContacts={totalContacts}
          />

          <TableCard
            footer={
              <OffsetPagination
                currentPage={currentPage ?? 0}
                totalItems={totalContacts}
                pageSize={pageSize}
                onNextPage={handleNextPage}
                onPrevPage={handlePrevPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isLoading={isLoading}
                label="contacts"
              />
            }
          >
            <ContactTable
              contacts={contacts}
              isLoading={isLoading}
              selectedContacts={selectedContacts}
              onSelectAll={handleSelectAll}
              onSelectContact={handleSelectContact}
              onEdit={handleEdit}
              onDelete={handleDeleteContact}
              onDeleteSelected={handleDeleteSelected}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortDirection}
              onAddTags={() => setIsAddTagsModalOpen(true)}
            />
          </TableCard>
        </div>
      </div>

      <ContactFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
      />

      <AddTagsModal
        isOpen={isAddTagsModalOpen}
        onClose={() => setIsAddTagsModalOpen(false)}
        selectedContacts={selectedContacts}
        onSuccess={refetch}
      />
    </Container>
  );
};

export default CallsDashboard;
